import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Main } from '@expo/html-elements';

import { getA11ySelector, useA11ySelector } from '@sg/a11y-helpers';
import {
  Alert,
  Container,
  FadeView,
  HStack,
  theme,
  useResponsive,
  useSafeFocusEffect,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
  useScrollspy,
} from '@sg/garnish';
import { MenuDietaryPreferences } from '@sg/shared-fe/features/menu';

import {
  MenuCategoriesGrid,
  MenuFloatingButtons,
  MenuOnlineOrderingUnavailable,
} from '@order/components/menu';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { telemetryLocationFromInput, useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { useBraze } from '../../../../context/Braze';
import { useSetLastInteractedMenuStore } from '../../../../context/LastInteractedStore';
import { useCart, useCustomScreenTitle, useDeliveryOrderInFlight } from '../../../../hooks';
import { useReorderIfRequired } from '../../../../screens';
import { AppFooter } from '../../../AppFooter';
import { useDietaryPreferencesState } from '../../../DietaryRestrictions';
import { useMenuContentNavigation, type useProductDetailsContentData } from '../../hooks';
import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';
import {
  MenuContentDietaryPreferencesModal,
  useMenuContentDietaryPreferencesModal,
} from '../MenuContentDietaryPreferencesModal';
import { MenuContentHeader } from '../MenuContentHeader';
import { MenuContentNavigationHeader } from '../MenuContentNavigationHeader';
import { MenuContentProductCard } from '../MenuContentProductCard';
import { MenuContentTopDownModal } from '../MenuContentTopDownModal';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentReady = (props: MenuContentReadyProps) => {
  const {
    restaurantData,
    deliveryDetailsData,
    orderChannel,
    locationName,
    productDetailsContentData,
  } = props;

  const { minWidth, match, currentBreakpoint } = useResponsive();
  const navigation = useSafeNavigation();
  const { formatMessage } = useIntl();
  const { lineItemsCount } = useCart();
  const { trackEvent } = useTelemetry();
  const { logCustomEvent } = useBraze();

  const locationHeaderA11ySelector = useA11ySelector('menu.location-header');

  const routeParams = useSafeRouteParams<{ referrer?: string }>();

  const { referrer } = routeParams ?? {};

  // ─────────────────────────────────────────────────────────────────────

  const {
    menu,
    hours,
    id: restaurantId,
    name: restaurantName,
    slug: restaurantSlug,
    isAcceptingOrders = true,
    notAcceptingOrdersReason,
  } = restaurantData;

  // NOTE: We need to hide empty categories
  const filteredCategories = useMemo(
    () => menu.categories.filter((category) => category.products.length > 0),
    [menu.categories],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isTopDownCategoriesModalEnabled = useFeatureFlag(
    'CELS-3094-menu-top-down-categories-enabled',
  );
  const isMacronutrientsEnabled = useFeatureFlag('CELS-4323-order-macronutrients-enabled');
  const isProductDetailsPageEnabled = useFeatureFlag(
    'CELS-4497-order-product-details-page-enabled',
  );

  const isOutpost = orderChannel === 'outpost';

  const shouldRenderNavigationHeader = match([false, true]);
  const shouldShowBagFloatingButton = currentBreakpoint.isXS && lineItemsCount > 0;

  const shouldRenderHoursMessage = isOutpost && typeof hours.formatted === 'string';

  const shouldRenderTopDownCategoriesModal =
    referrer !== 'bag' &&
    referrer !== 'reorder-confirm-location' &&
    isTopDownCategoriesModalEnabled &&
    currentBreakpoint.isXS;

  // ─── State ───────────────────────────────────────────────────────────

  const {
    hasOpenedDietaryPreferences,
    setHasOpenedDietaryPreferencesState,
    isResolvingDietaryPreferencesState,
  } = useDietaryPreferencesState();

  const {
    isDietaryPreferencesModalVisible,
    showDietaryPreferencesModal,
    hideDietaryPreferencesModal,
  } = useMenuContentDietaryPreferencesModal();

  const [isShowingMenuCategoriesModal, setIsShowingMenuCategoriesModal] = useState(
    shouldRenderTopDownCategoriesModal,
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const {
    generateCustomizationLink,
    generateCustomizationHref,
    setTargetParam,
    defaultActiveTargetId,
    changeLocation,
  } = useMenuContentNavigation({
    orderChannel,
    restaurantSlug,
    deliveryDetailsData,
  });

  const onContactUs = useCallback(() => {
    trackEvent({ name: 'menu.contact_us' });
  }, [trackEvent]);

  const hideMenuCategoriesModal = useCallback(() => {
    setIsShowingMenuCategoriesModal(false);
  }, []);

  const showTopDownModal = useCallback(() => {
    setIsShowingMenuCategoriesModal(true);
  }, []);

  // NOTE: Our version of Xstate causes the Scrollspy feature to break on code
  //       change when hot modules replacement is enabled.
  //       This will be fixed when we update to a newer version.
  //       Until then, reload the app to fix it.
  const { helpers, activeTargetId, setActiveTargetId, sharedValues } = useScrollspy({
    onActiveTargetIdChange: setTargetParam,
    defaultActiveTargetId: defaultActiveTargetId ?? filteredCategories?.[0]?.id,
  });

  const onNavItemPress = useCallback(
    (categoryId: string) => {
      const targetCategory = filteredCategories.find((category) => category.id === categoryId);

      if (!targetCategory) return;

      setActiveTargetId(categoryId);

      trackEvent({
        name: 'menu.jump_nav',
        payload: { id: restaurantSlug, tabName: targetCategory.name },
      });
    },
    [filteredCategories, restaurantSlug, setActiveTargetId, trackEvent],
  );

  const onDietaryPreferencesModalClose = useCallback(() => {
    hideDietaryPreferencesModal();
    void setHasOpenedDietaryPreferencesState();
  }, [hideDietaryPreferencesModal, setHasOpenedDietaryPreferencesState]);

  // NOTE: In case of `undefined`, the default `goBack` action will be used.
  const customBackButtonHandler =
    shouldRenderTopDownCategoriesModal && !isShowingMenuCategoriesModal
      ? showTopDownModal
      : undefined;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();

  const openBag = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/bag');
      return;
    }

    navigation?.navigate('Modal', { screen: 'Bag' });
  }, [isExpoRouterEnabled, router, navigation]);

  const trackMenuViewBrazeEvent = useCallback(() => {
    logCustomEvent('menu.view');
  }, [logCustomEvent]);

  useDeliveryOrderInFlight();
  useReorderIfRequired(restaurantSlug, deliveryDetailsData?.addressId);

  useSetLastInteractedMenuStore({
    restaurantId,
    restaurantName,
    restaurantSlug,
    addressId: deliveryDetailsData?.addressId,
    deliveryFee: deliveryDetailsData?.deliveryFee,
    vendor: deliveryDetailsData?.vendor,
    vendorRestaurantId: deliveryDetailsData?.vendorRestaurantId,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({
    name: 'menu.view',
    payload: { restaurant: telemetryLocationFromInput(restaurantData) },
  });

  /**
   * Only the smallest breakpoint uses a custom header, thus we need to
   * toggle the native header.
   */
  useLayoutEffect(() => {
    navigation?.setOptions({ headerShown: shouldRenderNavigationHeader });
  }, [navigation, shouldRenderNavigationHeader]);

  useCustomScreenTitle(
    formatMessage(messages.orderChannelAndLocationText, {
      order_channel: orderChannel,
      location: locationName,
    }),
  );

  useSafeFocusEffect(trackMenuViewBrazeEvent);

  // ─── Styles ──────────────────────────────────────────────────────────

  const infoMessagesContainerStyles = [
    styles.infoMessagesContainer,
    match([styles.infoMessagesContainerXS, styles.infoMessagesContainerSM]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.container}>
        {/* ─── Navigation Header ────────────────────────────────────────── */}

        <MenuContentNavigationHeader
          orderChannel={orderChannel}
          locationName={locationName}
          onBackBtnPressOverride={customBackButtonHandler}
        />

        {/* ─── Header ───────────────────────────────────────────────────── */}

        <MenuContentHeader
          menuGridScrollOffsetSV={sharedValues.scrollViewOffset}
          restaurantData={restaurantData}
          locationName={locationName}
          orderChannel={orderChannel}
          deliveryDetailsData={deliveryDetailsData}
          onContactUs={onContactUs}
          {...locationHeaderA11ySelector.props}
        >
          <MenuCategoriesGrid.Nav
            testID="menu-content.categories-nav"
            register={helpers.navScrollView.register}
            deregister={helpers.navScrollView.deregister}
            trackScroll={helpers.navScrollView.trackScroll}
            storeSize={helpers.navScrollView.storeSize}
          >
            {filteredCategories.map((category) => (
              <MenuCategoriesGrid.NavItem
                key={category.id}
                category={category.name}
                register={helpers.navItem.register}
                deregister={helpers.navItem.deregister}
                targetId={category.id}
                onPress={onNavItemPress}
                isActive={activeTargetId === category.id}
              />
            ))}
          </MenuCategoriesGrid.Nav>
        </MenuContentHeader>

        {/* ─── Categories ───────────────────────────────────────────────── */}

        <MenuCategoriesGrid.Container
          testID="menu-content.categories-container"
          register={helpers.scrollView.register}
          deregister={helpers.scrollView.deregister}
          trackScroll={helpers.scrollView.trackScroll}
          storeSize={helpers.scrollView.storeSize}
          storeContentSize={helpers.scrollView.storeContentSize}
        >
          <Main style={styles.contentContainer}>
            <Container>
              {filteredCategories.map((category, index) => {
                const isFirstCategory = index === 0;
                const isCustomCategory = category.isCustom;

                const shouldRenderStoreHoursMessage = isFirstCategory && shouldRenderHoursMessage; // prettier-ignore
                const shouldRenderDietaryPreferencesToggle = isFirstCategory && !isResolvingDietaryPreferencesState && !hasOpenedDietaryPreferences; // prettier-ignore
                const shouldRenderInfoMessages = shouldRenderStoreHoursMessage || shouldRenderDietaryPreferencesToggle; // prettier-ignore

                const a11ySelector = getA11ySelector('menu.categories-grid.category.container', {
                  group: category.name.toLowerCase(),
                  index,
                });

                // ─── Custom Category ───────────────────────────────────────────

                if (isCustomCategory) {
                  const customProducts = category.products.filter((product) => product.isCustom);

                  if (customProducts.length === 0) return null;

                  const hasMultipleProducts = customProducts.length > 1;
                  const itemsPerRow = hasMultipleProducts ? match([1, 1, 2]) : 1;

                  return (
                    <MenuCategoriesGrid.Category
                      key={category.id}
                      targetId={category.id}
                      register={helpers.target.register}
                      deregister={helpers.target.deregister}
                      {...a11ySelector.props}
                    >
                      <HStack itemsPerRow={itemsPerRow} gap={theme.spacing['4']}>
                        {customProducts.map((product) => (
                          <MenuCategoriesGrid.CustomProductCard
                            key={product.id}
                            product={product}
                            generateLink={generateCustomizationLink}
                            generateHref={generateCustomizationHref}
                          />
                        ))}
                      </HStack>
                    </MenuCategoriesGrid.Category>
                  );
                }

                // ─── Standard Category ─────────────────────────────────────────

                return (
                  <MenuCategoriesGrid.Category
                    key={category.id}
                    targetId={category.id}
                    register={helpers.target.register}
                    deregister={helpers.target.deregister}
                    {...a11ySelector.props}
                  >
                    {/* NOTE: We use the first category to render extra components */}

                    {shouldRenderInfoMessages ? (
                      <View style={infoMessagesContainerStyles}>
                        {shouldRenderStoreHoursMessage ? (
                          <Alert.Container palette="info">
                            <Alert.Text>{hours.formatted}</Alert.Text>
                          </Alert.Container>
                        ) : null}

                        {shouldRenderDietaryPreferencesToggle ? (
                          <MenuDietaryPreferences.Toggle onPress={showDietaryPreferencesModal} />
                        ) : null}
                      </View>
                    ) : null}

                    <MenuCategoriesGrid.CategoryHeading>
                      {category.name}
                    </MenuCategoriesGrid.CategoryHeading>

                    <MenuCategoriesGrid.ProductsStack>
                      {category.products.map((product) => {
                        const { id } = product;

                        const productDetailsContentDataItem = productDetailsContentData?.get(product.baseProduct.slug); // prettier-ignore
                        const hasProductDetails = Boolean(productDetailsContentDataItem); // prettier-ignore

                        const productDetailsData = productDetailsContentDataItem?.fields; // prettier-ignore
                        const cardHoverImage = productDetailsData?.cardHoverImage; // prettier-ignore
                        const cardHoverStaticImage = productDetailsData?.cardHoverStaticImage; // prettier-ignore
                        const carousel = productDetailsData?.carousel;

                        const hoverImageUrl = cardHoverImage?.fields?.file?.url;
                        const hoverStaticImageUrl = cardHoverStaticImage?.fields?.file?.url; // prettier-ignore
                        const hoverImageAlt = cardHoverImage?.fields?.title;

                        return (
                          <MenuContentProductCard
                            key={id}
                            product={product}
                            hasProductDetails={hasProductDetails}
                            restaurantSlug={restaurantSlug}
                            deliveryDetails={deliveryDetailsData}
                            generateLink={generateCustomizationLink}
                            generateHref={generateCustomizationHref}
                            imageFetchingPriority={isShowingMenuCategoriesModal ? 'low' : 'normal'} // prettier-ignore
                            shouldShowNutritionData={isMacronutrientsEnabled}
                            shouldShowNutritionLink={isProductDetailsPageEnabled} // prettier-ignore
                            hoverImageUrl={hoverImageUrl}
                            hoverStaticImageUrl={hoverStaticImageUrl}
                            hoverImageAlt={hoverImageAlt}
                            carousel={carousel}
                          />
                        );
                      })}
                    </MenuCategoriesGrid.ProductsStack>
                  </MenuCategoriesGrid.Category>
                );
              })}
            </Container>
          </Main>

          {minWidth.isSM ? <AppFooter /> : null}
        </MenuCategoriesGrid.Container>
      </View>

      {/* ─── Modals ─────────────────────────────────────────────────────── */}

      <MenuContentDietaryPreferencesModal
        isVisible={isDietaryPreferencesModalVisible}
        onClose={onDietaryPreferencesModalClose}
      />

      {isAcceptingOrders ? null : (
        <MenuOnlineOrderingUnavailable.Container>
          <MenuOnlineOrderingUnavailable.Dialog>
            <MenuOnlineOrderingUnavailable.Message message={notAcceptingOrdersReason} />
            <MenuOnlineOrderingUnavailable.Button onPress={changeLocation} />
          </MenuOnlineOrderingUnavailable.Dialog>
        </MenuOnlineOrderingUnavailable.Container>
      )}

      {shouldRenderTopDownCategoriesModal ? (
        <MenuContentTopDownModal
          orderChannel={orderChannel}
          locationName={locationName}
          categories={filteredCategories}
          isVisible={isShowingMenuCategoriesModal}
          hideModal={hideMenuCategoriesModal}
          onCategoryPress={setActiveTargetId}
        />
      ) : null}

      {/* ─── Floating Buttons ───────────────────────────────────────────── */}

      <FadeView show={shouldShowBagFloatingButton}>
        <MenuFloatingButtons.Bag lineItemsCount={lineItemsCount} onPress={openBag} />
      </FadeView>
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryEtaText: {
    defaultMessage: 'Delivers in {eta} minutes',
    description: 'Menu | Header | Delivery ETA text',
  },
  orderChannelAndLocationText: {
    defaultMessage: `{order_channel, select,
      pickup {Pickup from {location}}
      delivery {Delivery to {location}}
      outpost {Outpost at {location}}
      other {}
    }`,
    description: 'Menu | Navigation Header | Order channel and location text',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing['16'],

    // required for web `<Main>` component

    display: 'flex',
    flexDirection: 'column',
  },

  infoMessagesContainer: {
    alignItems: 'flex-start',
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['8'],
  },
  infoMessagesContainerXS: {
    marginTop: -theme.spacing['4'],
  },
  infoMessagesContainerSM: {
    marginTop: -theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentReadyProps = {
  restaurantData: MenuContentRestaurantData;
  productDetailsContentData: ProductDetailsContentData;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
  locationName: string;
};

type ProductDetailsContentData = ReturnType<
  typeof useProductDetailsContentData
>['productDetailsContentData'];
